import React, { useState } from 'react';
import CyberBadge from '../common/CyberBadge';
import { Shield, CheckCircle, XCircle, Lock, Unlock, Eye, Filter, Plus } from 'lucide-react';

export default function CaptainsView({
  captains = [],
  onSelectDrawer,
  onApproveCaptain,
  onRejectCaptain,
  onBlockCaptain,
  onUnblockCaptain,
  onOpenUnlockModal,
  onLockModule,
  onToggleSubscription,
}) {
  const [filterStatus, setFilterStatus] = useState('all');

  const isCapApproved = (c) => (c.status === 'active' || c.status === 'approved' || c.approvalStatus === 'approved') && !c.isBlocked && c.status !== 'blocked';
  const isCapBlocked = (c) => c.isBlocked === true || c.status === 'blocked' || c.approvalStatus === 'blocked';
  const isCapPending = (c) => !isCapApproved(c) && !isCapBlocked(c);

  const filteredCaptains = captains.filter((c) => {
    if (filterStatus === 'all') return true;
    if (filterStatus === 'pending') return isCapPending(c);
    if (filterStatus === 'approved') return isCapApproved(c);
    if (filterStatus === 'blocked') return isCapBlocked(c);
    return true;
  });

  return (
    <div className="animate-fade" style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <h1 style={{ fontSize: '1.75rem', fontWeight: 900, color: 'var(--text-primary)', letterSpacing: '-0.02em', margin: 0, display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Shield size={28} style={{ color: 'var(--neon-purple)' }} /> Captain Node Directory ({filteredCaptains.length})
          </h1>
          <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginTop: '4px' }}>
            Review captain registrations, issue module licenses, and manage host credentials.
          </p>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="subnav-pills">
        {['all', 'pending', 'approved', 'blocked'].map((statusKey) => (
          <button
            key={statusKey}
            className={`subnav-pill ${filterStatus === statusKey ? 'active' : ''}`}
            onClick={() => setFilterStatus(statusKey)}
          >
            <Filter size={14} /> {statusKey.toUpperCase()}
          </button>
        ))}
      </div>

      {/* Captains Table */}
      <div className="cyber-card" style={{ padding: '0', overflow: 'hidden' }}>
        <div className="table-container" style={{ border: 'none' }}>
          <table className="table">
            <thead>
              <tr>
                <th>CAPTAIN NAME</th>
                <th>CONTACT INFO</th>
                <th>CITY</th>
                <th>UNLOCKED MODULES</th>
                <th>STATUS</th>
                <th>ACTIONS</th>
              </tr>
            </thead>
            <tbody>
              {filteredCaptains.length === 0 ? (
                <tr>
                  <td colSpan="6" style={{ textAlign: 'center', padding: '40px 20px', color: 'var(--text-muted)' }}>
                    No captain accounts found matching criteria.
                  </td>
                </tr>
              ) : (
                filteredCaptains.map((cap) => {
                  const approved = isCapApproved(cap);
                  const blocked = isCapBlocked(cap);
                  const pending = isCapPending(cap);

                  return (
                    <tr key={cap._id || cap.id}>
                      <td style={{ fontWeight: 800, color: 'var(--text-primary)' }}>
                        {cap.fullName || cap.name || 'Unnamed Captain'}
                      </td>
                      <td>
                        <div style={{ fontSize: '0.85rem', color: 'var(--text-primary)' }}>{cap.email || 'N/A'}</div>
                        <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{cap.phone || 'No phone'}</div>
                      </td>
                      <td style={{ fontWeight: 600 }}>{cap.city || 'Indore'}</td>
                      <td>
                        {cap.unlockedModules && cap.unlockedModules.length > 0 ? (
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                            {cap.unlockedModules.map((mod, idx) => (
                              <span key={idx} className="badge badge-purple" style={{ fontSize: '0.65rem' }}>
                                {typeof mod === 'string' ? mod : mod.moduleName}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>No modules unlocked</span>
                        )}
                      </td>
                      <td>
                        <CyberBadge status={blocked ? 'blocked' : (approved ? 'approved' : 'pending')} />
                      </td>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                          <button
                            className="btn btn-outline"
                            style={{ padding: '6px 10px', fontSize: '0.75rem' }}
                            onClick={() => onSelectDrawer(cap, 'captain')}
                          >
                            <Eye size={13} /> View
                          </button>
                          <button
                            className="btn btn-purple"
                            style={{ padding: '6px 10px', fontSize: '0.75rem' }}
                            onClick={() => onOpenUnlockModal(cap)}
                          >
                            <Unlock size={13} /> License
                          </button>
                          {pending && onApproveCaptain && (
                            <button
                              className="btn btn-emerald"
                              style={{ padding: '6px 10px', fontSize: '0.75rem' }}
                              onClick={() => onApproveCaptain(cap._id || cap.id)}
                            >
                              <CheckCircle size={13} /> Approve
                            </button>
                          )}
                          {approved && onToggleSubscription && (
                            <button
                              className={`btn ${cap.vendor_subscription_active ? 'btn-outline' : 'btn-purple'}`}
                              style={{ padding: '6px 10px', fontSize: '0.75rem' }}
                              onClick={() => onToggleSubscription(cap._id || cap.id, cap.vendor_subscription_active)}
                            >
                              {cap.vendor_subscription_active ? <XCircle size={13} /> : <CheckCircle size={13} />}
                              {cap.vendor_subscription_active ? 'Revoke PRO' : 'Grant PRO'}
                            </button>
                          )}
                          {blocked ? (
                            onUnblockCaptain && (
                              <button
                                className="btn btn-emerald"
                                style={{ padding: '6px 10px', fontSize: '0.75rem' }}
                                onClick={() => onUnblockCaptain(cap._id || cap.id)}
                              >
                                <Unlock size={13} /> Unblock
                              </button>
                            )
                          ) : (
                            onBlockCaptain && (
                              <button
                                className="btn btn-danger"
                                style={{ padding: '6px 10px', fontSize: '0.75rem' }}
                                onClick={() => onBlockCaptain(cap._id || cap.id)}
                              >
                                <Lock size={13} /> Suspend
                              </button>
                            )
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
